export interface HighlightColor {
  value: string;
  label: string;
}

export const HIGHLIGHT_ORANGE = "#fdba8c";
export const HIGHLIGHT_GREEN = "#bcf0da";
export const HIGHLIGHT_YELLOW = "#fce96a";
export const HIGHLIGHT_BLUE = "#bcd3eb";

/** The palette of colors a user can choose from when highlighting text. */
export const HIGHLIGHT_COLORS: HighlightColor[] = [
  { value: HIGHLIGHT_ORANGE, label: "Orange" },
  { value: HIGHLIGHT_GREEN, label: "Green" },
  { value: HIGHLIGHT_YELLOW, label: "Yellow" },
  { value: HIGHLIGHT_BLUE, label: "Blue" },
];

/** Yellow; used whenever no valid palette color is available. */
export const DEFAULT_HIGHLIGHT_COLOR = "#fce96a";

/**
 * Normalizes a stored annotation color for display. Colors outside the
 * palette — including the legacy hardcoded "#FFFF00" and missing values —
 * resolve to the default yellow. Display-time only; stored data is never
 * rewritten.
 * @param color Color from a stored annotation, as an RGB hex code (e.g. "#fce96a")
 * @returns The matching palette color, or the default yellow
 */
export function resolveHighlightColor(color?: string | null): string {
  const normalized = color?.toLowerCase();
  const match = HIGHLIGHT_COLORS.find(
    (highlightColor) => highlightColor.value === normalized,
  );
  return match?.value ?? DEFAULT_HIGHLIGHT_COLOR;
}

/**
 * Styles for epubjs's rendition.annotations.highlight(). Mostly opaque so the
 * pastel palette stays true to the swatches (it is nearly invisible at
 * epubjs's default 0.3), with a touch of translucency to soften it over the
 * text. Only valid SVG presentation attributes belong here (marks-pane
 * applies them with setAttribute); the mix-blend-mode that keeps text legible
 * under the fill is CSS, applied to the overlay in EpubViewer.scss.
 * @param color Highlight color, as an RGB hex code
 * @returns SVG presentation attributes to pass to epubjs
 */
const HIGHLIGHT_FILL_OPACITY = "0.4";

export function epubHighlightStyles(color: string): Record<string, string> {
  return {
    fill: color,
    "fill-opacity": HIGHLIGHT_FILL_OPACITY,
  };
}

/**
 * CSS color for showing a highlight outside the book (e.g. the swatches in
 * the Bookmarks & Notes list), at the same opacity as the epub highlights.
 * @param color Color from a stored annotation, as an RGB hex code
 * @returns An rgba() CSS color string
 */
export function translucentHighlightColor(color?: string | null): string {
  const resolved = resolveHighlightColor(color);
  const red = parseInt(resolved.slice(1, 3), 16);
  const green = parseInt(resolved.slice(3, 5), 16);
  const blue = parseInt(resolved.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${HIGHLIGHT_FILL_OPACITY})`;
}

const MOST_RECENT_HIGHLIGHT_COLOR_STORAGE_KEY = "mostRecentHighlightColor";

/**
 * The palette color the user last saved a highlight with, falling back to
 * the default yellow when nothing valid has been stored.
 * @returns A palette color, as an RGB hex code
 */
export function readMostRecentHighlightColor(): string {
  return resolveHighlightColor(
    localStorage.getItem(MOST_RECENT_HIGHLIGHT_COLOR_STORAGE_KEY),
  );
}

/**
 * Remembers the color of the user's most recently saved highlight. Values
 * outside the palette are ignored so bad data never replaces a valid choice.
 * @param colorValue A palette color, as an RGB hex code
 */
export function storeMostRecentHighlightColor(colorValue: string): void {
  if (resolveHighlightColor(colorValue) !== colorValue) return;
  localStorage.setItem(MOST_RECENT_HIGHLIGHT_COLOR_STORAGE_KEY, colorValue);
}
